import { isPlatformBrowser } from "@angular/common";
import { Component, Inject, Input, PLATFORM_ID, Self, TemplateRef, SimpleChanges, OnChanges } from "@angular/core";
import { AbstractControl, ControlValueAccessor, NgControl } from "@angular/forms";
import { UploadMultipleFilesService } from "@fiduciaria/RequestCreation/Services/upload-multiple-files.service";
import { DocumentFile } from "@fiduciaria/RequestCreation/Types/document-file";
import { BsModalService, BsModalRef } from "ngx-bootstrap/modal";
import { NgxFileDropEntry, FileSystemFileEntry } from "ngx-file-drop";
import { Lightbox } from "ngx-lightbox";
import { Subject } from "rxjs";
import { first } from "rxjs/operators";

import { Unsubscriber } from "@shared/Components/Unsubscriber/unsubscriber.component";
import { ModalTitles, ModalType } from "@shared/Constantes/modal.constantes";
import { File } from "@shared/Entities/General/file.entity";
import { FileHandlerService } from "@shared/Services/fileHandler.service";
import { ModalService } from "@shared/Services/modal.service";
import { isMobile } from "@shared/Utils/device.util";

import { DocumentsPreviewModalComponent } from "../../../app/Fiduciaria/RequestCreation/Components/Documents/documents-preview-modal/documents-preview-modal.component";

import { InputType } from "./file-input.type";
import { ImageCapturerComponent } from "./image-capturer/image-capturer.component";

//TODO: MAKE THIS COMPONENT A VALID UNIFORMFIELDCONTROL
@Component({
  selector: "uni-file-input",
  templateUrl: "./file-input.component.html",
  styleUrls: ["./file-input.component.scss"],
})
export class UniFileInputComponent extends Unsubscriber implements ControlValueAccessor, OnChanges {
  @Input() public accept: string;
  @Input() public reference: string;
  @Input() public placeHolder: string;
  @Input() public popover?: string | TemplateRef<unknown>;
  @Input() public captureImage: boolean;
  @Input() public fileExample?: TemplateRef<unknown>;
  @Input() public fileLimit?: number;
  @Input() public isFiduciary?: boolean;
  @Input() public isFiduciaryRequest?: boolean;

  @Input() public fileName?: string;
  @Input() public showFileDetails: boolean;
  @Input() public FileNameToName: (file: File) => string;
  @Input() public FileNameToExtension: (file: File) => string;
  @Input() public GetFileSizeMegaBytes: (file: File, digits: number) => number;
  @Input() public OpenUploadDocumentsModal: (reference: string) => void;
  @Input() public SetCurrentFilesListEvent: (files: DocumentFile[]) => void;
  @Input() public SetcurrentFilesUploadAllEvent: (files: DocumentFile[]) => void;
  @Input() private requestFiles?: DocumentFile[];
  @Input() private _firstFile?: DocumentFile = null;

  private setFirstFile(firstFile: DocumentFile): void {
    if (firstFile && firstFile.name === this.reference && firstFile?.file) {
      const exists: boolean = this.files.some((f) => f.name === firstFile.name);
      if (!exists) this.setNewFile(firstFile.file.object, firstFile.file.name);
    }
  }

  public multiple: boolean = false;

  @Input() public set type(newType: InputType) {
    this._type = newType;
    this.multiple = newType === "list";
  }
  public get type(): InputType {
    return this._type;
  }
  private _type: InputType = "inline";

  public files: File[] = [];
  public disabled: boolean;
  public file = new Subject<File | File[]>();

  public isImage: boolean;

  public isCapturing: boolean = false;

  @Input()
  public get required(): boolean {
    const validators = this.ngControl?.control?.validator;
    return this._required ?? validators ? validators({} as AbstractControl)?.hasOwnProperty("required") : false;
  }
  public set required(value: boolean) {
    this._required = value;
  }
  protected _required: boolean | undefined;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouchedCallback = (): void => {};

  constructor(
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private platform: Object,
    private fileHandlerService: FileHandlerService,
    private _lightbox: Lightbox,
    private bsModalService: BsModalService,
    private modalService: ModalService,
    private bsModalRef: BsModalRef,
    private uploadMultipleFilesService: UploadMultipleFilesService,
    @Self() public ngControl: NgControl
  ) {
    super();

    if (this.ngControl) {
      // Note: we provide the value accessor through here, instead of
      // the `providers` to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?._firstFile) {
      const firstFile: DocumentFile = changes?._firstFile?.currentValue as DocumentFile;
      this.setFirstFile(firstFile);
    }
  }

  public onFileDropped(files: NgxFileDropEntry[]): void {
    if (this.fileLimit && this.files.length > this.fileLimit) {
      this.modalService.showModalGeneral({
        title: ModalTitles.warning,
        body: `Has superado el límite de ${this.fileLimit} archivos permitidos.`,
        type: ModalType.warning,
      });
      return;
    }

    if (!files[0]?.fileEntry?.isFile) return;

    const fileEntry: FileSystemFileEntry = files[0].fileEntry as FileSystemFileEntry;

    fileEntry.file((file) => {
      const size = this.bytesToMegaBytes(file.size);
      const [invalid, errorMsg] = this.checkFileSize(size);

      if (invalid) {
        this.modalService.showModalGeneral({
          type: ModalType.warning,
          title: ModalTitles.warning,
          body: `${errorMsg}.`,
        });
        return;
      }

      this.setNewFile(file, fileEntry.name);
    });
  }

  private checkFileSize(size: number): [invalid: boolean, errorMsg: string] {
    let errorMsg: string = "";
    let invalid: boolean;
    const service = this.uploadMultipleFilesService;
    const [lessThan4MB, lessThan20MB, lessThan25MB, lessThan125MB]: [boolean, boolean, boolean, boolean] = [
      size <= 4,
      size <= 20,
      size <= service.getFileSizeMegaBytesLessThan,
      size <= service.getRequestFilesSizeMegaBytesLessThan,
    ];

    if (this.isFiduciary && this.isFiduciaryRequest) {
      errorMsg = !lessThan125MB ? service.requestFilesSizeMegaBytesLessThanMsg : errorMsg;
      errorMsg = lessThan125MB && !lessThan25MB ? service.fileSizeMegaBytesLessThanMsg : errorMsg;
      invalid = !lessThan125MB || !lessThan25MB;

      errorMsg += `<br/><br/>Tamaño actual: ${service.roundSizeValue(size, 3)} MBS`;
    } else if (this.isFiduciary && !this.isFiduciaryRequest) {
      errorMsg = !lessThan20MB ? "El archivo no puede superar los 20 MBS" : errorMsg;
      invalid = !lessThan20MB;
    } else {
      errorMsg = lessThan4MB ? "El archivo no puede superar los 4 MBS" : errorMsg;
      invalid = !lessThan4MB;
    }

    return [invalid, errorMsg];
  }

  public setNewFile(file: globalThis.File, name: string): void {
    const extention = name.split(".").pop();
    this.fileHandlerService.getBase64ArrayFromBuffer(file).subscribe((array) => {
      const newFile: File = {
        object: file,
        name: this.fileName ? `${this.fileName}.${extention}` : name,
        buffer: this.fileHandlerService.stripBase64StuffFromImage(array),
      };

      let invalid: boolean = false;
      if (this.isFiduciary && this.isFiduciaryRequest) {
        invalid = this.validateRequestFilesSize(newFile);
      }

      if (!invalid) {
        this.isImage = this.checkIfImage(newFile);
        this.files = this.multiple ? [...this.files, newFile] : [newFile];
        this.file.next(this.multiple ? this.files : newFile);
      }
    });
  }

  private validateRequestFilesSize(file: File): boolean {
    const service: UploadMultipleFilesService = this.uploadMultipleFilesService;
    this.requestFiles = this.requestFiles ?? [];
    const documentfile: DocumentFile = { name: this.reference, file: file };

    const files = this.requestFiles.concat(documentfile);
    const validations = service.validateFilesSizeMegaBytes(files);

    service.filesSizeValidationsAlert(validations);

    return validations[1];
  }

  private checkIfImage(file: File): boolean {
    const extension = file.name.split(".").pop();
    const imageExtentions = ["png", "jpg", "jpeg", "bmp", "avif"];
    const isImage = file.object.type.includes("image") && imageExtentions.includes(extension);
    return isImage && !file.object.type.includes("svg");
  }

  private bytesToMegaBytes(bytes: number): number {
    return this.uploadMultipleFilesService.bytesToMegaBytes(bytes);
  }

  public removeFile(file: File): void {
    this.files = this.files.filter((currentFile) => currentFile !== file);
    this.file.next(this.multiple ? this.files : null);
    this.clearCurrentFiles();
  }

  private openDocumentPreviewModal(file: File): void {
    this.bsModalRef = this.bsModalService.show(DocumentsPreviewModalComponent, {
      ignoreBackdropClick: true,
      class: "modal-lg",
      initialState: { file } as Partial<DocumentsPreviewModalComponent>,
    });
  }

  public onClickUploadFile(openFileSelector: () => void): void {
    if (this.captureImage && isPlatformBrowser(this.platform) && "mediaDevices" in navigator && !isMobile()) {
      if (!this.isCapturing) {
        navigator.mediaDevices
          .getUserMedia({ video: true })
          .then((stream) => {
            this.onCaptureImage(stream);
          })
          .catch(openFileSelector);
      }
      this.isCapturing = true;
    } else {
      openFileSelector();
    }
  }

  private onKeyPressEmptyFunc(): void {
    return;
  }

  public onCaptureImage(stream: MediaStream): void {
    const capturerModalRef = this.bsModalService.show(ImageCapturerComponent, {
      class: "modal-lg",
      initialState: { stream },
      ignoreBackdropClick: true,
    });

    this.anotherSubscription = capturerModalRef.content.captureImage.pipe(first()).subscribe((base64: string) => {
      const blob = this.fileHandlerService.b64toBlob(base64);
      const file = new globalThis.File([blob], "Capture.png", { type: "image/png" });
      this.setNewFile(file, "Capture.png");
      capturerModalRef.hide();
    });

    this.anotherSubscription = capturerModalRef.onHidden.subscribe(() => {
      this.isCapturing = false;
    });
  }

  public onClickFile(file: File): void {
    if (this.fileHandlerService.isImage(file))
      this._lightbox.open([{ src: this.fileHandlerService.addBase64StuffToImage(file.buffer), caption: file.name, thumb: null }]);
  }

  public writeValue(value: File | string): void {
    if (typeof value === "string") {
      const extention = value.split(".").pop();
      this.fileHandlerService.getFileFromURL(value, `${this.fileName}.${extention}`).then((file: globalThis.File) => {
        if (!file) return;
        this.setNewFile(file, `file.${extention}`);
      });
    } else {
      this.setFile(value);
    }
  }

  private setFile(value: File): void {
    this.files = !value ? [] : this.multiple ? [...this.files, value] : [value];
    this.file.next(this.multiple ? this.files : value);
  }

  public registerOnChange(fn: (value: File | File[]) => void): void {
    this.file.asObservable().subscribe(fn);
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private getFilesByReference(): DocumentFile[] {
    return this.uploadMultipleFilesService.getFilesByReference(this.reference);
  }

  private getFilesSizeMegaBytes1x1(files: DocumentFile[]): number {
    return this.uploadMultipleFilesService.getFilesSizeMegaBytes1X1(files, 3);
  }

  private getFilesSizesMegaBytesByReference(): number {
    return this.uploadMultipleFilesService.getFilesSizesMegaBytesByReference(this.reference);
  }

  private getFilesRoundSizesMegaBytesByReference(digits: number): number {
    const sizeBytes: number = this.uploadMultipleFilesService.getFilesSizesBytesByReference(this.reference);
    return this.uploadMultipleFilesService.getSizeMegaBytes(sizeBytes, digits);
  }

  private getFilesCountByReference(): number {
    return this.uploadMultipleFilesService.getFilesCountByReference(this.reference);
  }

  private clearCurrentFiles(): void {
    if (this.SetCurrentFilesListEvent) {
      this.SetCurrentFilesListEvent(this.uploadMultipleFilesService.filesList.filter((x) => x.name !== this.reference));
    }

    if (this.SetcurrentFilesUploadAllEvent) {
      this.SetcurrentFilesUploadAllEvent(
        this.uploadMultipleFilesService.currentFilesUploaded.filter((x) => x.name !== this.reference)
      );
    }
  }
}
